import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import Modal from "components/Common/Modal";

import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { useTour } from "@reactour/tour";
import { useGetPartnersQuery } from "api/api-accounts";
import { useFetchRmQuery } from "api/api-sales";
import {
  useGetCurrentUserQuery,
  useUpdateUserMutation,
} from "api/api-users-v2";
import banner from "assets/banner.png";
import Support from "assets/support.svg?react";
import TradingPartners from "assets/tradingPartners.svg?react";
import {
  BUYER,
  DASHBOARD,
  NEW_USER,
  ONBOARDING,
  PRESALES,
  REGISTERED,
  TR_LINK,
  BOOK_MEETING,
} from "codes";
import { PERMISSIONS } from "permissions";
import SkeletonLoad from "components/Common/SkeletonLoad";
import history from "helpers/history";
import useIsMobile from "hooks/useIsMobile";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppSelector } from "redux/hooks";

import { InfoCard, type InfoCardProps } from "components/Common/InfoCard/index";
import { v4 as randomUUID } from "uuid";
import CountryEligibilityModal from "../CountryEligibilityModal";
import DashBoardBody from "../DashboardBody";

import styles from "./dashboardheader.module.scss";

const DashboardHeader = () => {
  const { push } = history;
  const theme = useTheme();
  const [show, setShow] = useState(false);
  const onClose = () => setShow(false);

  const [eventUri, setEventUri] = useState("");

  const currentTime = moment();

  const { data: user } = useGetCurrentUserQuery();

  const USER_ROLE = useAppSelector((state) => state.appState.role);

  const APP_STATE = useAppSelector((state) => state.appState.value);

  const isMobile = useIsMobile();
  const env = import.meta.env.VITE_ENV;

  const { t } = useTranslation();
  const { setIsOpen } = useTour();
  const [nextCall, setNextCall] = useState(currentTime.unix());
  const [canMeet, setCanMeet] = useState(true);

  const expirationTime = moment(currentTime).add(1, "hour");
  const expirationTimestamp = expirationTime.unix();
  const [updateUser, { isUninitialized, isLoading }] = useUpdateUserMutation();

  const RESTRICTED_STATES = [NEW_USER, REGISTERED];
  const {
    data: partners,
    isFetching: isFetchingPartners,
    isUninitialized: isPartnersUninitialized,
  } = useGetPartnersQuery(USER_ROLE.id ?? "", {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
    //check for ts status
    pollingInterval: 300000,
    skip: !Boolean(USER_ROLE.id),
  });
  const profile = useAppSelector((state) => state.appState.profile);

  const { data: rm } = useFetchRmQuery(USER_ROLE.id, {
    skip: !Boolean(USER_ROLE.id),
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const setTime = () => {
    if (user && isUninitialized)
      updateUser({
        account_id: USER_ROLE.id,
        user: {
          id: user?.id,
          settings: {
            ...user?.settings,
            [user?.first_name]: {
              [DASHBOARD]: {
                timeTillNextCall: expirationTimestamp,
              },
            },
          },
        },
      })
        .then(() => {
          setCanMeet(false);
          setNextCall(expirationTimestamp);
        })
        .catch((error) => {
          const errorFile = error?.message;
          toast(`Something went wrong : ${errorFile}`, {
            type: "error",
          });
        });
  };

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      setTime();
      setEventUri(e.data.payload.event.uri);
    },
  });

  const dashboardSettings = user?.settings?.[user?.first_name]?.[DASHBOARD];
  const isOnCoolDown = moment
    .unix(
      dashboardSettings?.hasOwnProperty("timeTillNextCall")
        ? dashboardSettings["timeTillNextCall"]
        : nextCall
    )
    .isAfter(currentTime);

  useEffect(() => {
    if (isOnCoolDown && USER_ROLE.id) {
      setCanMeet(false);
    } else setCanMeet(true);
  }, [isOnCoolDown, canMeet]);

  let overviewData: InfoCardProps[] = [
    {
      title: `${t("trading-partners")}`,
      subtitle: partners?.length
        ? `${partners?.length ?? 0} ${
            profile === BUYER.toLowerCase() ? "Supplier" : "Buyer"
          }${partners?.length > 1 ? "s" : ""} Listed`
        : "No trading partners listed",
      onClick: () => push(TR_LINK),
      backgroundColor: theme.palette.background.default,
      horizontal: true,
      headingProps: {
        variant: isMobile ? "h2" : "h3",
        fontWeight: "bolder",
        fontSize: isMobile ? "1em" : "1.5em",
      },

      borderRadius: "10px",
      titleContainerSpacing: isMobile ? 0 : 0.5,
      sidebarChildren: (
        <TradingPartners
          style={{ fontSize: "4em", width: "100%", height: "auto" }}
        />
      ),
      bottomColorBand: "1ch solid #808285",
      height: isMobile ? 70 : 120,
    },
  ];

  const [openBookCalendar, setOpenBookCalendar] = useState(false);
  const userPermissions = useAppSelector((state) => state.appState.user_permission);

  const bookAble = Boolean(canMeet && rm?.url && partners?.length);
  const IS_POST_SALES = (state: string) =>
    ![NEW_USER, REGISTERED, PRESALES].includes(state);

  // Check if user has any of the new meeting permissions
  const hasNewMeetingPermissions = userPermissions.some((permission) => 
    [
      PERMISSIONS.book_meeting_risk_manager,
      PERMISSIONS.book_meeting_support_manager,
      PERMISSIONS.book_meeting_rm_manager
    ].includes(permission)
  );

  // Enhanced booking cards that include both RM booking and new multi-manager booking
  let callBooking: InfoCardProps[] = [];

  // Add RM booking card if available (existing functionality)
  if (bookAble) {
    callBooking.push({
      title: eventUri ? "View RM Booking" : `${t("book-a-call")}`,
      subtitle: (
        <Typography
          sx={{
            fontSize: "1em",
            [theme.breakpoints.down("md")]: {
              fontSize: "0.8em",
            },
          }}
        >
          {`${t("with-your-relationship-manager")} ${rm?.rm_name ?? ""}`}
        </Typography>
      ),
      onClick: () => bookAble && setOpenBookCalendar(true),
      style: {
        color: theme.palette.primary.main,
      },
      backgroundColor: theme.palette.background.default,
      horizontal: true,
      headingProps: {
        variant: isMobile ? "h2" : "h3",
        fontWeight: "bolder",
        fontSize: isMobile ? "1em" : "1.5em",
      },
      borderRadius: "10px",
      titleContainerSpacing: isMobile ? 0 : 0.5,
      sidebarChildren: (
        <Support
          style={{ fontSize: "3em", width: "75%", height: "auto" }}
        />
      ),
      bottomColorBand: `1ch solid ${theme.palette.error.main}`,
      height: isMobile ? 70 : 120,
    });
  }

  // Always show the general booking option for all users (new improved booking flow)
  // For REGISTERED users: shows support only
  // For other users: shows tabs with different managers
  const shouldShowGeneralBooking = !RESTRICTED_STATES.includes(APP_STATE);
  
  if (shouldShowGeneralBooking) {
    const bookingTitle = APP_STATE === REGISTERED ? "Book a Meeting with Support" : "Book a Meeting";
    const bookingSubtitle = APP_STATE === REGISTERED 
      ? "Get help with onboarding and platform setup"
      : "Connect with our specialists for expert guidance";
    
    callBooking.push({
      title: bookingTitle,
      subtitle: (
        <Typography
          sx={{
            fontSize: "1em",
            [theme.breakpoints.down("md")]: {
              fontSize: "0.8em",
            },
          }}
        >
          {bookingSubtitle}
        </Typography>
      ),
      onClick: () => history.push(BOOK_MEETING),
      style: {
        color: theme.palette.primary.main,
      },
      backgroundColor: theme.palette.background.default,
      horizontal: true,
      headingProps: {
        variant: isMobile ? "h2" : "h3",
        fontWeight: "bolder",
        fontSize: isMobile ? "1em" : "1.5em",
      },
      borderRadius: "10px",
      titleContainerSpacing: isMobile ? 0 : 0.5,
      sidebarChildren: (
        <Support
          style={{ fontSize: "3em", width: "75%", height: "auto" }}
        />
      ),
      bottomColorBand: `1ch solid ${theme.palette.success.main}`,
      height: isMobile ? 70 : 120,
    });
  }

  if (Boolean(USER_ROLE?.role))
    overviewData = [
      {
        title: `${t("trading-partners")}`,
        subtitle: partners?.length
          ? `${partners?.length ?? 0} ${
              profile === BUYER.toLowerCase() ? "Supplier" : "Buyer"
            }${partners?.length > 1 ? "s" : ""} Listed`
          : "No trading partners listed",

        onClick: () => push(TR_LINK),
        backgroundColor: theme.palette.background.default,
        horizontal: true,
        headingProps: {
          variant: isMobile ? "h2" : "h3",
          fontWeight: "bolder",
          fontSize: isMobile ? "1em" : "1.5em",
        },
        titleContainerSx: {
          padding: isMobile ? "1ch" : "1ch 2ch",
        },
        borderRadius: "10px",
        titleContainerSpacing: isMobile ? 0 : 0.5,
        sidebarChildren: (
          <TradingPartners
            style={{ fontSize: "4em", width: "100%", height: "auto" }}
          />
        ),
        bottomColorBand: `1ch solid ${theme.palette.error.main}`,
        height: isMobile ? 70 : 120,
      },
    ];

  return (
    <Fragment>
      <Grid item xs={12} lg={11} margin="auto">
        {APP_STATE !== ONBOARDING && (
          <InfoCard
            className={styles.card}
            title={t("welcome") ?? ""}
            subtitle={
              <Typography
                fontSize="0.8em"
                sx={{ paddingRight: isMobile ? 0 : "5ch" }}
              >
                Convert your invoices into cash on Incomlend's invoice financing
                marketplace, freely choosing how many export receivables you
                would like to submit.
              </Typography>
            }
            headingProps={{
              variant: isMobile ? "h2" : "h3",
              fontWeight: "bolder",
              fontSize: isMobile ? 20 : "2em",
              color: theme.palette.white.main,
            }}
            titleContainerSpacing={1}
            cardActionArea={false}
            bodyProps={{ color: theme.palette.white.main }}
            backgroundImageSrc={banner}
            padding={isMobile ? "2ch 1ch 4ch 1ch" : "2ch"}
            borderRadius="10px"
            backgroundColor="#001E61"
            height={isMobile ? 70 : 120}
          />
        )}
      </Grid>

      {!RESTRICTED_STATES.includes(APP_STATE) && (
        <Grid item xs={11} lg={11} margin="auto">
          <Grid container spacing={1} justifyContent="space-between">
            <Grid item xs={8} lg={6} id="account-overview">
              <Typography
                fontWeight="bolder"
                fontSize={isMobile ? "1.3em" : "1.5em"}
                variant="h3"
              >
                {t("overview")}
              </Typography>
            </Grid>
            {(env === "local" || env === "dev") && ( //Enable only for testing/dev env
              <Grid item>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setIsOpen(true)}
                >
                  Tour!
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {overviewData &&
                  overviewData.map((item) => (
                    <Grid
                      key={
                        typeof item.title === "string"
                          ? item.title
                          : `item-${randomUUID()}`
                      }
                      item
                      xs={12}
                      lg={4}
                    >
                      <InfoCard {...item} />
                    </Grid>
                  ))}
                {callBooking.map((item) => (
                  <Grid key={randomUUID()} item xs={12} lg={4}>
                    {isLoading && <SkeletonLoad bars={1} />}
                    {!isLoading && <InfoCard {...item} />}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
          {openBookCalendar && (
            <Grid item xs={12} justifyContent="center" alignContent="center">
              <Modal
                width={isMobile ? "100vw" : "45vw"}
                height="85vh"
                modalOverlaySx={{
                  background: "white !important",
                  borderRadius: "3ch",
                }}
                message={
                  rm?.url ? (
                    <InlineWidget
                      url={rm?.url}
                      styles={{
                        width: "100%",
                        height: "auto",
                      }}
                    />
                  ) : (
                    <SkeletonLoad bars={3} />
                  )
                }
                open={openBookCalendar}
                onClose={() => setOpenBookCalendar(false)}
                closeButton
              />
            </Grid>
          )}
        </Grid>
      )}
      <Grid item xs={11} lg={11} margin="auto" mb={2}>
        <Grid container spacing={1} justifyContent="space-between">
          <Grid item xs={12} lg={8}>
            {(IS_POST_SALES(APP_STATE) || APP_STATE === PRESALES) && <DashBoardBody />}
            {/* <ExportFactoringFeesCalculator /> */}
          </Grid>

          <Grid item xs={12} lg={4}>
            <Grid container spacing={2}>
              {APP_STATE === REGISTERED && (
                <Grid item xs={12} id="track-your-cargo">
                  <Card
                    sx={{
                      backgroundImage: `url(${banner})`,
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item>
                          <ArrowCircleRightIcon
                            sx={{
                              color: theme.palette.white.main,
                              mt: 1,
                              fontSize: "1.5em",
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              <Grid item xs={12} id="check-country-eligibility">
                <Card
                  sx={{
                    backgroundImage: `url(${banner})`,
                    backgroundColor: "#ced4da",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <CardContent>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        <ArrowCircleRightIcon
                          sx={{
                            color: theme.palette.white.main,
                            mt: 1,
                            fontSize: "1.5em",
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography
                          fontWeight="bold"
                          fontSize="1.3em"
                          sx={{
                            cursor: "pointer",
                            color: theme.palette.white.main,
                          }}
                          onClick={() => setShow(true)}
                        >
                          Check Country Eligibility
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} id="faq">
                <Card
                  sx={{
                    backgroundImage: `url(${banner})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <CardContent>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item>
                        <ArrowCircleRightIcon
                          sx={{
                            color: theme.palette.white.main,
                            mt: 1,
                            fontSize: "1.5em",
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Typography
                          fontWeight="bold"
                          fontSize="1.3em"
                          sx={{
                            cursor: "pointer",
                            color: theme.palette.white.main,
                          }}
                          onClick={() => history.push("/knowledge-centre")}
                        >
                          Knowledge Centre
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ bgcolor: theme.palette.background.default }}>
                  <iframe
                    width="100%"
                    height="205"
                    src="https://www.youtube.com/embed/LlfGlJCmTYc?si=eywy5gTrY25ux0UN"
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                  <CardContent>
                    <Typography fontWeight="bold" fontSize="1em">
                      Watch how Invoice Financing helped Rahul (exporter) and
                      James (importer) in their trade.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <CountryEligibilityModal show={show} onClose={onClose} />
    </Fragment>
  );
};

export default DashboardHeader;
